import { useApplicantAPI, useFormikSubmit } from "@certn/api";
import {
  CheckboxFormikField,
  Container,
  Divider,
  Grid,
  GridItem,
  Loader,
  PhoneNumberInputField,
  PrimaryButton,
  SectionMessage,
  TextInputFormikField,
} from "@certn/cascadia-react";
import { Form, Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { bool, object, string } from "yup";

import { AppTitle } from "../components/AppTitle";
import { ClickwrapCheckBox } from "../components/click-wrap";
import { Schemas } from "../types";
import {
  getApplicantPortalTermsOfUseUrl,
  getApplicantRegistrationPrivacyPolicyUrl,
} from "@certn/utils";
import { NameOptOutField, NameOptOutToggle } from "./NameOptOutToggle";

interface Props {
  onSuccess: () => void;
  subjectId?: string;
}

type FormValues = Schemas["CreatePersonalProfile"];

export function ApplicantDetailsPage(props: Props) {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const applicantLanguage = searchParams.get("applicant_language");

  const shouldLoadRegistrationPrefill = Boolean(props.subjectId);
  const { data: registrationPrefill, isLoading: isRegistrationPrefillLoading } =
    useApplicantAPI(
      shouldLoadRegistrationPrefill
        ? "/api/internal/applicant/me/registration-prefill/"
        : null,
      shouldLoadRegistrationPrefill
        ? { subject_id: props.subjectId }
        : undefined,
      { shouldRetryOnError: false }
    );
  const isWaitingForRegistrationPrefill =
    shouldLoadRegistrationPrefill && isRegistrationPrefillLoading;

  const { submit } = useFormikSubmit("/api/internal/applicant/me/create/", {
    onSuccess: props.onSuccess,
    showToastOnError: true,
  });

  // 1+ letters with spaces, apostrophes and hyphens allowed anywhere except first and last character
  const validNameRegex = /^((\p{L}+)|(\p{L}[\p{L}\s'-]*\p{L}))$/u;
  const validationText = t("Please only use letters and numbers");

  const requiredUnlessOptedOut = (
    optOutField: NameOptOutField,
    message: string
  ) =>
    string()
      .trim()
      .matches(validNameRegex, {
        message: validationText,
        excludeEmptyString: true,
      })
      .when(optOutField, {
        is: (optedOut?: boolean) => !optedOut,
        then: (schema) => schema.required(message),
      });

  const schema = object({
    given_name: string()
      .trim()
      .required(t("Enter your first or given name"))
      .matches(validNameRegex, validationText),
    additional_name: requiredUnlessOptedOut(
      "has_no_additional_name",
      t("Enter your middle names or turn on “I don’t have a middle name”")
    ),
    has_no_additional_name: bool(),
    family_name: requiredUnlessOptedOut(
      "has_no_family_name",
      t(
        "Enter your last or family name or turn on “I don’t have a last or family name”"
      )
    ),
    has_no_family_name: bool(),
    phone_number: string().required(t("Enter phone number.")),
    has_consented_to_marketing: bool(),
    agreed_to_terms_and_conditions: bool().oneOf(
      [true],
      t<string>("Please agree to Certn's Privacy Policy and Terms to continue.")
    ),
    subject_id: string(),
  });

  const pageTitle = (
    <AppTitle
      title={t("Let's get you set up")}
      subtitle={t(
        "Enter your name exactly as it appears on your government-issued photo ID."
      )}
    />
  );

  if (isWaitingForRegistrationPrefill) {
    return (
      <Container layout="applicant/narrow">
        {pageTitle}
        <Loader />
      </Container>
    );
  }

  return (
    <Container layout="applicant/narrow">
      {pageTitle}
      <Formik<FormValues>
        initialValues={{
          given_name: registrationPrefill?.given_name ?? "",
          additional_name: registrationPrefill?.additional_name ?? "",
          family_name: registrationPrefill?.family_name ?? "",
          has_no_additional_name: false,
          has_no_family_name: false,
          phone_number: registrationPrefill?.phone_number ?? "",
          has_consented_to_marketing: false,
          agreed_to_terms_and_conditions: false,
          privacy_policy_url: getApplicantRegistrationPrivacyPolicyUrl(
            i18n.language
          ),
          applicant_terms_url: getApplicantPortalTermsOfUseUrl(i18n.language),
          subject_id: props.subjectId,
          applicant_language: applicantLanguage,
        }}
        onSubmit={(values, helpers) =>
          submit(
            {
              ...values,
              privacy_policy_url: getApplicantRegistrationPrivacyPolicyUrl(
                i18n.language
              ),
              applicant_terms_url: getApplicantPortalTermsOfUseUrl(
                i18n.language
              ),
            },
            helpers
          )
        }
        validationSchema={schema}
      >
        {({ status, isSubmitting, handleSubmit, values }) => {
          return (
            <Form>
              <Grid>
                {status && (
                  <GridItem>
                    <SectionMessage
                      type="danger"
                      sectionTitle={t("System Error")}
                      icon="info"
                    >
                      {status}
                    </SectionMessage>
                  </GridItem>
                )}
                <GridItem>
                  <TextInputFormikField
                    isRequired
                    label={t("First or given name")}
                    description={t(
                      "Your first given name only. Add any other given names in the next field."
                    )}
                    name="given_name"
                    autoComplete="given-name"
                    testID="sign-up-form-legal-first-name"
                  />
                </GridItem>
                <GridItem>
                  <TextInputFormikField
                    isRequired
                    label={t("Middle names")}
                    description={t(
                      "Any other given names, in the order they appear on your ID."
                    )}
                    name="additional_name"
                    autoComplete="additional-name"
                    isDisabled={values.has_no_additional_name === true}
                    testID="sign-up-form-legal-middle-name"
                  />
                  <NameOptOutToggle
                    optOutField="has_no_additional_name"
                    nameField="additional_name"
                    testID="sign-up-form-no-middle-name"
                  >
                    {t("I don’t have a middle name")}
                  </NameOptOutToggle>
                </GridItem>
                <GridItem>
                  <TextInputFormikField
                    isRequired
                    label={t("Last or family name")}
                    description={t(
                      "Include every part, such as hyphenated or two-part names. On a passport, this is the name under “Surname”. Don’t include suffixes such as Jr., Sr. or III."
                    )}
                    name="family_name"
                    autoComplete="family-name"
                    isDisabled={values.has_no_family_name === true}
                    testID="sign-up-form-legal-last-name"
                  />
                  <NameOptOutToggle
                    optOutField="has_no_family_name"
                    nameField="family_name"
                    testID="sign-up-form-no-last-name"
                  >
                    {t("I don’t have a last or family name")}
                  </NameOptOutToggle>
                </GridItem>
                <GridItem>
                  <PhoneNumberInputField
                    name="phone_number"
                    label={t("Phone number")}
                    autoComplete="tel"
                    isRequired
                    testID="sign-up-form-phone-number"
                    type="string"
                  />
                </GridItem>
                {/* Keeps the required terms checkbox visually apart from the name toggles */}
                <GridItem>
                  <Divider testID="sign-up-form-consent-divider" />
                </GridItem>
                <GridItem>
                  <CheckboxFormikField
                    name="has_consented_to_marketing"
                    testID="sign-up-form-news-and-offers"
                  >
                    <Trans>
                      I want to receive news, feature updates, discounts, and
                      offers from Certn. (Optional)
                    </Trans>
                  </CheckboxFormikField>
                </GridItem>
                <GridItem>
                  <ClickwrapCheckBox testID="sign-up-form-PP-and-t&cs" />
                </GridItem>
                <GridItem>
                  <PrimaryButton
                    fullWidth
                    onPress={handleSubmit}
                    size="large"
                    variant="brand-primary"
                    isLoading={isSubmitting}
                    testID="sign-up-form-continue-button"
                  >
                    {t("Continue")}
                  </PrimaryButton>
                </GridItem>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
}
